import { UzBadge, type UzColumn, UzSwitch, UzTable } from '@utazzo/ui';
import React from 'react';

import type { ManagedWhatsAppTemplate } from '@/features/whatsapp/whatsapp.api';

import {
  useSetWhatsAppTemplateHidden,
  useWhatsAppTemplatesManage,
} from '@/features/whatsapp/use-whatsapp';

/**
 * WhatsApp tab: every Meta-APPROVED template on the DoubleTick account, with
 * a per-template switch controlling whether it appears in the chat widget's
 * template picker. Wording itself is authored/approved in DoubleTick - this
 * only curates what agents can hand-send; hidden templates stay sendable by
 * the notification engine (automated booking messages etc.).
 *
 * Visibility persists in app_config (`whatsapp.picker_hidden_templates`) and
 * reaches every agent's picker within seconds.
 */
export const WhatsAppTemplatesTab = (): React.JSX.Element => {
  const manageQuery = useWhatsAppTemplatesManage(true);
  const setHidden = useSetWhatsAppTemplateHidden();

  const data = manageQuery.data?.templates ?? [];

  const columns: ReadonlyArray<UzColumn<ManagedWhatsAppTemplate>> = [
    {
      id: 'template',
      header: 'Template',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-zinc-900">{row.label}</span>
          <span className="font-mono text-xs text-zinc-500">{row.name}</span>
        </div>
      ),
    },
    {
      id: 'params',
      header: 'Body params',
      cell: (row) =>
        row.params.length === 0 ? (
          <span className="text-xs text-zinc-400">none</span>
        ) : (
          <span className="text-xs text-zinc-600">{row.params.join(', ')}</span>
        ),
    },
    {
      id: 'buttons',
      header: 'Dynamic button',
      cell: (row) =>
        row.buttonParams && row.buttonParams.length > 0 ? (
          <UzBadge tone="secondary" pill>
            {row.buttonParams.join(', ')}
          </UzBadge>
        ) : (
          <span className="text-xs text-zinc-400">-</span>
        ),
      width: '200px',
    },
    {
      id: 'visible',
      header: 'Show in chat picker',
      cell: (row) => (
        <UzSwitch
          checked={!row.hidden}
          disabled={setHidden.isPending}
          onCheckedChange={(checked) => setHidden.mutate({ name: row.name, hidden: !checked })}
        />
      ),
      width: '160px',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <p className="max-w-3xl text-xs leading-relaxed text-zinc-500">
        Every approved template on the DoubleTick account. The switch controls only the chat
        widget&apos;s template picker (live for all agents within seconds) - hidden templates remain
        sendable by automated notifications. Template wording is authored and approved in
        DoubleTick; new templates appear here automatically once Meta approves them.
      </p>
      <UzTable
        columns={columns}
        data={data}
        getRowId={(row) => row.name}
        loading={manageQuery.isLoading}
      />
    </div>
  );
};
